import { checkSmallBusinessLimit, ownInvoiceOpenCents, ownInvoiceRevenue, ownInvoiceRevenueAll, smallBusinessBasis, smallBusinessRule, smallBusinessTurnover, type OwnInvoiceRow, type OwnRevenue } from "@talentkontor/domain";

/**
 * Zahlen der Übersicht im Creator-Konto (0178), rein und testbar: Umsatz und Zahlungen aus den eigenen Rechnungen an Marken.
 * Umsatz zählt nach der gemeinsamen Regel des Domain-Pakets (ownInvoiceRevenue, Praxistest K-254): festgeschriebene
 * Rechnungen nach Rechnungsdatum, netto, ohne Storno-Paare und ohne Verzugskosten-Belege; dieselbe Zahl steht unter
 * Einstellungen · Firma. Offen und überfällig sind versendete, nicht bezahlte Rechnungen mit positivem Rest (brutto);
 * Stornorechnungen, Gutschriften und stornierte Rechnungen nie.
 */
export interface StudioRechnung extends OwnInvoiceRow {
  effective_due_date: string | null;
  paid_at: string | null;
}

/** Spalten, die studioZahlen braucht */
export const STUDIO_RECHNUNG_SPALTEN = "status, issue_date, net_cents, gross_cents, effective_due_date, paid_at, paid_cents, currency, credit_for, costs_for, cancelled_at, tax_mode, fx_rate_to_org";

/** K-294: Rechnungen in anderen Währungen, je Währung in ihrer eigenen Währung */
export interface WeitereWaehrung {
  waehrung: string;
  umsatzJahrCents: number;
  offenCents: number;
  ueberfaelligCents: number;
  /** als bezahlt erfasst in diesem Jahr (brutto) */
  bezahltJahrCents: number;
}

export interface StudioZahlen {
  umsatzJahrCents: number;
  umsatzVorjahrCents: number;
  bezahltJahrCents: number;
  offenCents: number;
  offenAnzahl: number;
  ueberfaelligCents: number;
  ueberfaelligAnzahl: number;
  /** Umsatz je Jahr mit Inlandsanteil (für die Kleinunternehmergrenze), andere Währungen zum Versandkurs eingerechnet (K-279) */
  jahr: OwnRevenue;
  vorjahr: OwnRevenue;
  /** Rechnungen dieses Jahres in anderer Währung ohne eingefrorenen Kurs (für die Grenze nicht eingerechnet) */
  ohneKurs: number;
  /** K-294: Umsatz und Offenes in anderen Währungen */
  weitere: WeitereWaehrung[];
}

export function studioZahlen(rechnungen: readonly StudioRechnung[], heute: string, waehrung: string): StudioZahlen {
  const jahrNr = Number(heute.slice(0, 4));
  const eigene = ownInvoiceRevenue(rechnungen, jahrNr, waehrung);
  const eigeneVorjahr = ownInvoiceRevenue(rechnungen, jahrNr - 1, waehrung);
  const jahr = ownInvoiceRevenueAll(rechnungen, jahrNr, waehrung);
  const vorjahr = ownInvoiceRevenueAll(rechnungen, jahrNr - 1, waehrung);
  const weitere = new Map<string, WeitereWaehrung>();
  const z: StudioZahlen = {
    umsatzJahrCents: eigene.netCents, umsatzVorjahrCents: eigeneVorjahr.netCents, bezahltJahrCents: 0,
    offenCents: 0, offenAnzahl: 0, ueberfaelligCents: 0, ueberfaelligAnzahl: 0, jahr, vorjahr, ohneKurs: jahr.unconverted, weitere: [],
  };
  for (const w of eigene.otherCurrencies) weitere.set(w, { waehrung: w, umsatzJahrCents: ownInvoiceRevenue(rechnungen, jahrNr, w).netCents, offenCents: 0, ueberfaelligCents: 0, bezahltJahrCents: 0 });
  for (const r of rechnungen) {
    if (r.currency !== waehrung) {
      const w = weitere.get(r.currency) ?? { waehrung: r.currency, umsatzJahrCents: 0, offenCents: 0, ueberfaelligCents: 0, bezahltJahrCents: 0 };
      if (r.status === "paid" && !r.credit_for && (r.paid_at ?? "").slice(0, 4) === String(jahrNr)) { w.bezahltJahrCents += Number(r.gross_cents ?? 0); weitere.set(r.currency, w); }
      const offen = ownInvoiceOpenCents(r);
      if (offen <= 0) continue;
      w.offenCents += offen;
      if (r.effective_due_date && r.effective_due_date < heute) w.ueberfaelligCents += offen;
      weitere.set(r.currency, w);
      continue;
    }
    // Eingegangen: bezahlte Rechnungen (eine Stornorechnung wird nie „bezahlt“, eine stornierte Rechnung trägt den Status cancelled)
    if (r.status === "paid" && !r.credit_for && (r.paid_at ?? "").slice(0, 4) === String(jahrNr)) z.bezahltJahrCents += Number(r.gross_cents ?? 0);
    const offen = ownInvoiceOpenCents(r);
    if (offen <= 0) continue;
    z.offenCents += offen;
    z.offenAnzahl += 1;
    if (r.effective_due_date && r.effective_due_date < heute) {
      z.ueberfaelligCents += offen;
      z.ueberfaelligAnzahl += 1;
    }
  }
  z.weitere = [...weitere.values()].sort((a, b) => a.waehrung.localeCompare(b.waehrung));
  return z;
}

export interface Grenzstand {
  land: "DE" | "AT" | "CH" | "LI";
  /** AT misst brutto, DE, CH und LI netto */
  basis: "gross" | "net";
  /** Umsatz, der für die Grenze zählt (DE/AT nur Leistungsort Inland) */
  umsatzCents: number;
  vorjahrCents: number;
  /** Umsatz mit Leistungsort im Ausland (Reverse Charge EU, Drittland), der für DE/AT nicht zählt */
  auslandCents: number;
  /** Grenze im laufenden Jahr (Cent) und Anteil daran */
  grenzeCents: number;
  anteil: number;
  /** AT: einmalige Toleranz bis 60.500 € */
  toleranzCents: number | null;
  /** toleranz (nur AT): über 55.000 €, aber höchstens 60.500 €; dieses Jahr noch befreit */
  stufe: "ok" | "nah" | "toleranz" | "drueber";
  /** Vorjahresgrenze (DE 25.000 €, AT 55.000 €), null wo es keine gibt */
  vorjahresgrenzeCents: number | null;
  vorjahrDrueber: boolean;
  /** DE: der Umsatz dieses Jahres liegt über der Vorjahresgrenze, ab 1. Januar gilt die Regelbesteuerung */
  folgejahrDrueber: boolean;
  gesetz: string;
}

/** Kleinunternehmergrenze für das Land; null ohne hinterlegte Regel oder bei anderer Währung */
export function kleinunternehmerStand(land: string | null | undefined, waehrung: string, z: StudioZahlen): Grenzstand | null {
  const r = smallBusinessRule(land);
  if (!r || r.currency !== waehrung) return null;
  const umsatz = smallBusinessTurnover(r, z.jahr);
  const vorjahr = smallBusinessTurnover(r, z.vorjahr);
  const brutto = smallBusinessBasis(r) === "gross";
  const c = checkSmallBusinessLimit(r, umsatz, "current");
  const vorjahrCheck = r.previousCents !== null ? checkSmallBusinessLimit(r, vorjahr, "previous") : null;
  const swiss = r.country === "CH" || r.country === "LI";
  return {
    land: r.country,
    basis: brutto ? "gross" : "net",
    umsatzCents: umsatz,
    vorjahrCents: vorjahr,
    auslandCents: swiss ? 0 : brutto ? z.jahr.grossCents - z.jahr.domesticGrossCents : z.jahr.netCents - z.jahr.domesticNetCents,
    grenzeCents: r.currentCents,
    anteil: c.ratio,
    toleranzCents: r.toleranceCents,
    stufe: c.level === "over" ? "drueber" : c.level === "tolerance" ? "toleranz" : c.level === "near" ? "nah" : "ok",
    vorjahresgrenzeCents: r.previousCents,
    vorjahrDrueber: vorjahrCheck?.level === "over",
    folgejahrDrueber: r.previousCents !== null && r.previousCents < r.currentCents && umsatz > r.previousCents,
    gesetz: r.law,
  };
}
